/**
 * The players' chat (protocol: chat.channels, chat.history, chat.send, chat.subscribe, chat.unsubscribe, chat.markRead;
 * events chat.message, chat.deleted; the text rules: src/shared/chat-rules.ts).
 *
 * Channels: 'global' and 'clan:<clanId>'. Who may read a clan channel is decided by the clan directory (the one index of
 * membership, ./clan-directory.ts) at every request and at every delivery, so a player who left a clan or was removed
 * from it gets nothing more of its channel, whatever subscriptions or cursors it kept. Guests read; writing needs a
 * registered account of at least config.chat.minLevelToSend that no operator muted.
 *
 * Messages (C.chatMessages, one document per message): every channel keeps its last config.chat.historyGlobal /
 * historyClan messages in memory and in storage; the write of a new message deletes the ones it pushes out, in the same
 * storage batch. A message's `seq` is unique and strictly increasing within the server (microseconds of the server
 * clock, or one more than the last one), so read positions and cursors stay valid across restarts. The text is
 * sanitized by the shared rules and the operator's filter words are masked (config.chat.words); the unmasked text is
 * kept for the operators only (`original`). The sender's level is the message's own (as when it was written); its
 * nickname follows the account (a rename, also an operator's, changes the stored messages: `renamed`) and its clan is
 * shown as the clan is now (a renamed clan's new name, nothing once the clan is gone).
 *
 * Live delivery: a player subscribes to the channels it shows (chat.subscribe: the game while its chat panel is open);
 * a new message goes as chat.message to the channel's subscribers through the event hub (every connection of the
 * player, the replay buffer of events.resume included), never to the connection that sent it (its reply carries it).
 * Subscriptions live in memory only: they end with chat.unsubscribe, when the player has been offline for
 * config.chat.subscriptionGraceMs (a reconnect within it keeps them, events.resume replays what it missed; a player that
 * subscribes without being present, e.g. over HTTP without polling, starts that grace at once), and for a clan channel
 * as soon as the player is no longer a member (checked at delivery; the sweep drops them).
 *
 * Unread counts: a player's read position per channel (C.chatReads, one document per player): the global channel's and
 * its current clan's; a channel the player sees for the first time (a new player, a new clan) starts read up to its
 * newest message. Unread = messages after the position that others wrote. The positions change in memory and are
 * written behind (at most every READ_FLUSH_MS per player, with the server's flush at shutdown), so a reader that marks
 * every few seconds costs no storage write each time; the memory keeps the positions of present players only.
 *
 * Flood protection (in memory): per player at most config.chat.burst messages per windowMs, at least minIntervalMs
 * apart (RATE_LIMITED 'chat_rate_limited'), and not the same text (chatDuplicateKey) within duplicateWindowMs
 * (RATE_LIMITED 'chat_duplicate'); per client address (IPv6: its /64) at most config.chat.addressBurst messages per
 * windowMs of all its players together (RATE_LIMITED 'chat_rate_limited', details.scope 'address': several accounts
 * of one spammer). The router's request rate limit applies besides.
 *
 * Moderation (operator console, ../admin/modules/chat.ts): an operator deletes a message (chat.deleted to the
 * channel's subscribers) or mutes a player until a time (C.chatMutes; the sweep removes expired mutes). A clan's
 * channel goes with the clan (the sweep deletes the messages of channels whose clan no longer exists). Account
 * deletion: the player's messages, read positions and mute go in the deletion's batch (deletionOps); `forget` takes the
 * messages out of the channels, tells the subscribers (chat.deleted 'account_deleted') and deletes what a request that
 * was still under way stored after the batch (a send re-checks the account before it stores the message).
 */
import { BackendError } from '../../../shared/protocol/errors';
import type { ListPage } from '../../../shared/protocol/messages';
import type { ChatChannelInfo, ChatLimits, ChatMessage, ChatSendBlock, ClanBrief, PlayerId } from '../../../shared/protocol/model';
import { LIST_LIMITS } from '../../../shared/protocol/validators';
import { CHAT_GLOBAL, CHAT_TEXT_MAX, type ChatChannelId, chatDuplicateKey, checkChatText, clanChannel, maskChatWords, parseChatChannel } from '../../../shared/chat-rules';
import { addressLimitKey } from '../client-address';
import type { ConnectionContext } from '../hub';
import { badCursor, cursorInt, decodeCursor, encodeCursor, keysetPage, pageLimit } from '../pagination';
import { type StorageBatchOp, deleteOp, getOrSkipDamaged, putOp } from '../storage';
import type { ClanDirectory } from './clan-directory';
import { C, type ServiceDeps } from './deps';
import type { ProfileService } from './profile';

/** A stored message (C.chatMessages, id = messageId). */
export interface StoredChatMessage extends ChatMessage {
  v: 1;
  /** The sanitized text before the filter words were masked, when they masked something (the operators see it). */
  original?: string;
}

/** A player's read positions (C.chatReads, id = playerId). */
export interface ChatReadRecord {
  v: 1;
  playerId: PlayerId;
  global: number;
  /** The read position of the player's clan channel (another clan's position is replaced when the player changes clan). */
  clan: { clanId: number; seq: number } | null;
}

/** An operator's mute (C.chatMutes, id = playerId). */
export interface ChatMuteRecord {
  v: 1;
  playerId: PlayerId;
  /** Until when the player may not write (epoch ms). */
  until: number;
  at: number;
  /** The operator (username) and the reason of the console. */
  by: string;
  reason: string | null;
}

/** A message as the operator console lists it. */
export interface ChatMessageRow extends ChatMessage {
  original: string | null;
}

/** How often the sweep looks for channels of disbanded clans and expired mutes. */
const HOUSEKEEPING_MS = 60_000;
/** A changed read position is written at most this long after the change. */
export const READ_FLUSH_MS = 30_000;

export class ChatService {
  /** Channel -> its messages, oldest first. */
  private readonly channels = new Map<ChatChannelId, StoredChatMessage[]>();
  /** messageId -> channel. */
  private readonly channelOf = new Map<string, ChatChannelId>();
  /** Channel -> subscribed players, and player -> its channels. */
  private readonly subscribers = new Map<ChatChannelId, Set<PlayerId>>();
  private readonly subscriptions = new Map<PlayerId, Set<ChatChannelId>>();
  /** Subscribed players without a connection: since when (config.chat.subscriptionGraceMs). */
  private readonly offlineSince = new Map<PlayerId, number>();
  private readonly mutes = new Map<PlayerId, ChatMuteRecord>();
  private readonly reads = new Map<PlayerId, ChatReadRecord>();
  /** The player's recent messages (time and flood key), newest last, within the longest flood window. */
  private readonly recent = new Map<PlayerId, { at: number; key: string }[]>();
  /** The times of the recent messages of a client address (addressLimitKey), within windowMs. */
  private readonly recentByAddress = new Map<string, number[]>();
  /** Read positions changed in memory and not stored yet: since when. */
  private readonly dirtyReads = new Map<PlayerId, number>();
  private lastReadFlush = 0;
  private flushingReads: Promise<void> | null = null;
  private lastSeq = 0;
  private loading: Promise<void> | null = null;
  private lastHousekeeping = 0;

  constructor(
    private readonly d: ServiceDeps,
    private readonly profiles: ProfileService,
    private readonly directory: ClanDirectory,
  ) {}

  private get cfg() {
    return this.d.config.chat;
  }

  /** The rules clients see (system.hello limits.chat). */
  limits(): ChatLimits {
    const c = this.cfg;
    return {
      textMax: CHAT_TEXT_MAX,
      historyGlobal: c.historyGlobal,
      historyClan: c.historyClan,
      minLevelToSend: c.minLevelToSend,
      guestsCanSend: false,
      burst: c.burst,
      windowMs: c.windowMs,
      minIntervalMs: c.minIntervalMs,
      duplicateWindowMs: c.duplicateWindowMs,
    };
  }

  /** Loads the stored messages and mutes (once; every request awaits it). */
  init(): Promise<void> {
    this.loading ??= this.load();
    return this.loading;
  }

  private async load(): Promise<void> {
    const [messages, mutes] = await Promise.all([this.d.storage.list<StoredChatMessage>(C.chatMessages), this.d.storage.list<ChatMuteRecord>(C.chatMutes)]);
    for (const m of messages) {
      if (!m || typeof m.messageId !== 'string' || typeof m.channel !== 'string' || !Number.isSafeInteger(m.seq)) continue;
      this.channelState(m.channel).push(m);
      this.channelOf.set(m.messageId, m.channel);
      this.lastSeq = Math.max(this.lastSeq, m.seq);
    }
    const extra: StorageBatchOp[] = [];
    for (const [channel, list] of this.channels) {
      list.sort((a, b) => a.seq - b.seq);
      // A smaller history configured since: the oldest messages go.
      for (const m of list.splice(0, Math.max(0, list.length - this.keptIn(channel)))) {
        this.channelOf.delete(m.messageId);
        extra.push(deleteOp(C.chatMessages, m.messageId));
      }
    }
    for (const m of mutes) if (m && typeof m.playerId === 'string' && typeof m.until === 'number') this.mutes.set(m.playerId, m);
    for (let i = 0; i < extra.length; i += 500) await this.d.storage.batch(extra.slice(i, i + 500));
  }

  private keptIn(channel: ChatChannelId): number {
    return channel === CHAT_GLOBAL ? this.cfg.historyGlobal : this.cfg.historyClan;
  }

  private channelState(channel: ChatChannelId): StoredChatMessage[] {
    let list = this.channels.get(channel);
    if (!list) this.channels.set(channel, (list = []));
    return list;
  }

  private lastSeqOf(channel: ChatChannelId): number {
    const list = this.channels.get(channel);
    return list && list.length > 0 ? list[list.length - 1].seq : 0;
  }

  // ------------------------------------------------------------------------------------------------ access

  /** Whether the player reads the channel now (a clan channel: a member of that clan). */
  private mayRead(playerId: PlayerId, channel: ChatChannelId): boolean {
    const p = parseChatChannel(channel);
    if (!p) return false;
    return p.kind === 'global' || this.directory.clanIdOf(playerId) === p.clanId;
  }

  private async assertAccess(playerId: PlayerId, channel: ChatChannelId): Promise<void> {
    await this.init();
    await this.directory.ready();
    if (!this.mayRead(playerId, channel)) throw new BackendError('FORBIDDEN', 'Only the members of the clan read and write its chat', { reason: 'chat_not_member', channel });
  }

  /** The channels the player reads now: the global one, its clan's. */
  private channelsOf(playerId: PlayerId): ChatChannelId[] {
    const clanId = this.directory.clanIdOf(playerId);
    return clanId === null ? [CHAT_GLOBAL] : [CHAT_GLOBAL, clanChannel(clanId)];
  }

  /** The operator's mute of the player while it lasts. */
  muteOf(playerId: PlayerId): ChatMuteRecord | null {
    const m = this.mutes.get(playerId);
    return m && m.until > this.d.clock.now() ? { ...m } : null;
  }

  /** Why the player may not write now (null: it may). */
  private async sendBlock(playerId: PlayerId): Promise<{ block: ChatSendBlock; level: number } | { block: null; level: number; nickname: string }> {
    const card = await this.profiles.playerCard(playerId);
    if (card.guest) return { block: 'guest', level: card.level };
    if (this.muteOf(playerId)) return { block: 'muted', level: card.level };
    if (card.level < this.cfg.minLevelToSend) return { block: 'level', level: card.level };
    return { block: null, level: card.level, nickname: card.nickname };
  }

  // ------------------------------------------------------------------------------------------------ read positions

  /** The player's read positions, a channel seen for the first time read up to its newest message (stored then). */
  private async readsOf(playerId: PlayerId): Promise<ChatReadRecord> {
    let rec = this.reads.get(playerId) ?? (await this.d.storage.get<ChatReadRecord>(C.chatReads, playerId));
    let changed = false;
    if (!rec || typeof rec.global !== 'number') {
      rec = { v: 1, playerId, global: this.lastSeqOf(CHAT_GLOBAL), clan: null };
      changed = true;
    }
    const clanId = this.directory.clanIdOf(playerId);
    if (clanId !== null && rec.clan?.clanId !== clanId) {
      rec = { ...rec, clan: { clanId, seq: this.lastSeqOf(clanChannel(clanId)) } };
      changed = true;
    }
    this.reads.set(playerId, rec);
    if (changed) this.markDirty(playerId);
    return rec;
  }

  private markDirty(playerId: PlayerId): void {
    if (!this.dirtyReads.has(playerId)) this.dirtyReads.set(playerId, this.d.clock.now());
  }

  /**
   * Stores the read positions changed in memory (`all`: every one, else those changed READ_FLUSH_MS ago or earlier).
   * A player whose account is gone is skipped (a late request after its deletion leaves no document behind).
   */
  flushReads(all = true): Promise<void> {
    if (this.flushingReads) return this.flushingReads.then(() => (all ? this.flushReads(true) : undefined));
    const run = this.writeReads(all).finally(() => {
      this.flushingReads = null;
    });
    this.flushingReads = run;
    return run;
  }

  private async writeReads(all: boolean): Promise<void> {
    const now = this.d.clock.now();
    const due = [...this.dirtyReads].filter(([, since]) => all || now - since >= READ_FLUSH_MS).map(([pid]) => pid);
    const ops: StorageBatchOp[] = [];
    for (const pid of due) {
      this.dirtyReads.delete(pid);
      const rec = this.reads.get(pid);
      // (a damaged account: not stored, logged; the other players' read markers are)
      if (!rec || !(await getOrSkipDamaged(this.d.storage, C.accounts, pid, this.d.logger))) continue;
      ops.push(putOp(C.chatReads, pid, rec));
    }
    try {
      for (let i = 0; i < ops.length; i += 500) await this.d.storage.batch(ops.slice(i, i + 500));
    } catch (err) {
      // (stored at the next flush)
      for (const o of ops) if (o.op === 'put' && this.reads.has(o.id)) this.markDirty(o.id);
      throw err;
    }
  }

  private readSeqIn(rec: ChatReadRecord, channel: ChatChannelId): number {
    if (channel === CHAT_GLOBAL) return rec.global;
    const p = parseChatChannel(channel);
    return p && p.kind === 'clan' && rec.clan?.clanId === p.clanId ? rec.clan.seq : 0;
  }

  private withReadSeq(rec: ChatReadRecord, channel: ChatChannelId, seq: number): ChatReadRecord {
    if (channel === CHAT_GLOBAL) return { ...rec, global: seq };
    const p = parseChatChannel(channel);
    return p && p.kind === 'clan' ? { ...rec, clan: { clanId: p.clanId, seq } } : rec;
  }

  /** Messages of others after `readSeq` (not of the players the client hides). */
  private unreadIn(channel: ChatChannelId, readSeq: number, playerId: PlayerId, hidden: ReadonlySet<PlayerId> = new Set()): number {
    const list = this.channels.get(channel) ?? [];
    let lo = 0;
    let hi = list.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (list[mid].seq <= readSeq) lo = mid + 1;
      else hi = mid;
    }
    let n = 0;
    for (let i = lo; i < list.length; i++) if (list[i].playerId !== playerId && !hidden.has(list[i].playerId)) n++;
    return n;
  }

  /** Moves the player's read position of a channel forward (never back, never past its newest message). */
  private async moveRead(playerId: PlayerId, channel: ChatChannelId, seq: number): Promise<number> {
    return this.d.mutex.run(`chatr:${playerId}`, async () => {
      const rec = await this.readsOf(playerId);
      const cur = this.readSeqIn(rec, channel);
      const next = Math.max(cur, Math.min(seq, this.lastSeqOf(channel)));
      if (next !== cur) {
        this.reads.set(playerId, this.withReadSeq(rec, channel, next));
        this.markDirty(playerId);
      }
      return next;
    });
  }

  // ------------------------------------------------------------------------------------------------ player requests

  /** chat.channels */
  async channelsFor(playerId: PlayerId, exclude: readonly PlayerId[] = []): Promise<{ channels: ChatChannelInfo[]; mutedUntil: number | null }> {
    await this.init();
    await this.directory.ready();
    const rec = await this.d.mutex.run(`chatr:${playerId}`, () => this.readsOf(playerId));
    const send = await this.sendBlock(playerId);
    const subscribed = this.subscribedOf(playerId);
    const hidden = new Set(exclude);
    const channels = this.channelsOf(playerId).map((channel): ChatChannelInfo => {
      const p = parseChatChannel(channel)!;
      const readSeq = this.readSeqIn(rec, channel);
      return {
        channel,
        kind: p.kind,
        clan: p.kind === 'clan' ? this.directory.brief(p.clanId) : null,
        canSend: send.block === null,
        sendBlock: send.block,
        lastSeq: this.lastSeqOf(channel),
        readSeq,
        unread: this.unreadIn(channel, readSeq, playerId, hidden),
        subscribed: subscribed.includes(channel),
      };
    });
    return { channels, mutedUntil: this.muteOf(playerId)?.until ?? null };
  }

  /** chat.history: newest first. */
  async history(playerId: PlayerId, channel: ChatChannelId, opts: { limit?: number; cursor?: string } = {}): Promise<{ channel: string; items: ChatMessage[]; next: string | null }> {
    await this.assertAccess(playerId, channel);
    const limit = pageLimit(opts.limit, LIST_LIMITS['chat.history'].default, LIST_LIMITS['chat.history'].max);
    const tag = cursorTag(channel);
    let after: ((m: StoredChatMessage) => boolean) | null = null;
    if (opts.cursor !== undefined) {
      const [t, seqPart] = decodeCursor(opts.cursor, 'chat', 2);
      if (t !== tag) throw badCursor('chat history');
      const seq = cursorInt(seqPart);
      after = (m) => m.seq < seq;
    }
    const newest = [...(this.channels.get(channel) ?? [])].reverse();
    const page: ListPage<StoredChatMessage> = keysetPage(newest, limit, after, (m) => encodeCursor('chat', [tag, m.seq]));
    return { channel, items: page.items.map((m) => this.view(m)), next: page.next };
  }

  /**
   * room.chat (services/rooms.ts): a lobby room's message passes the same rules as chat.send - a guest, a muted player
   * or one below config.chat.minLevelToSend may not write, the text is checked (checkChatText: control, bidi and
   * invisible characters, length) and masked (maskChatWords), and the address has a flood limit (config.chat
   * .addressBurst per windowMs) of its own for the room chats: they never use up the budget of chat.send of the other
   * players behind the same address (a mobile operator's shared IPv4). The player's own pace is the room's limit
   * (config.rooms.chatBurst per chatWindowMs). Returns the text to deliver and `deliver`, which the room calls
   * synchronously right before it sends the message (after its own limit): it checks the address's limit again and
   * counts the message, so only a delivered message counts.
   */
  async vetRoomMessage(playerId: PlayerId, text: string, ctx?: ConnectionContext): Promise<{ text: string; deliver: () => void }> {
    await this.init();
    const send = await this.sendBlock(playerId);
    if (send.block === 'guest') throw new BackendError('FORBIDDEN', 'A guest account reads the chat only: sign up with a nickname to write', { reason: 'guest_not_allowed' });
    if (send.block === 'muted') throw new BackendError('FORBIDDEN', 'An operator muted this player in the chat', { reason: 'chat_muted', until: this.muteOf(playerId)?.until ?? this.d.clock.now() });
    if (send.block !== null) throw new BackendError('FORBIDDEN', 'The player is below the level that writes to the chat', { reason: 'chat_level_too_low', minLevel: this.cfg.minLevelToSend, level: send.level });
    const checked = checkChatText(text);
    if (!checked.ok) {
      const message = checked.reason === 'chat_text_empty' ? 'The message is empty' : `The message is longer than ${checked.max} characters`;
      throw new BackendError('VALIDATION_FAILED', message, { reason: checked.reason, field: 'text', max: checked.max });
    }
    const address = ctx?.remoteAddress ? `room:${addressLimitKey(ctx.remoteAddress)}` : null;
    this.roomAddressPace(address, this.d.clock.now(), false);
    return { text: maskChatWords(checked.text, this.cfg.words).text, deliver: () => this.roomAddressPace(address, this.d.clock.now(), true) };
  }

  /** RATE_LIMITED scope 'address' when the address's room messages reached config.chat.addressBurst; `count`: else counts one. */
  private roomAddressPace(address: string | null, now: number, count: boolean): void {
    const c = this.cfg;
    if (address === null || c.addressBurst <= 0) return;
    const recent = (this.recentByAddress.get(address) ?? []).filter((at) => now - at < c.windowMs);
    this.recentByAddress.set(address, recent);
    if (recent.length >= c.addressBurst) {
      throw new BackendError('RATE_LIMITED', 'Too many messages from this network', { reason: 'chat_rate_limited', scope: 'address', retryAfterMs: Math.max(1, c.windowMs - (now - recent[0])) });
    }
    if (count) recent.push(now);
  }

  /** chat.send */
  async send(playerId: PlayerId, p: { channel: ChatChannelId; text: string }, ctx?: ConnectionContext): Promise<{ message: ChatMessage }> {
    await this.assertAccess(playerId, p.channel);
    const send = await this.sendBlock(playerId);
    if (send.block === 'guest') throw new BackendError('FORBIDDEN', 'A guest account reads the chat only: sign up with a nickname to write', { reason: 'guest_not_allowed' });
    if (send.block === 'muted') throw new BackendError('FORBIDDEN', 'An operator muted this player in the chat', { reason: 'chat_muted', until: this.muteOf(playerId)?.until ?? this.d.clock.now() });
    if (send.block !== null) throw new BackendError('FORBIDDEN', 'The player is below the level that writes to the chat', { reason: 'chat_level_too_low', minLevel: this.cfg.minLevelToSend, level: send.level });
    const checked = checkChatText(p.text);
    if (!checked.ok) {
      const message = checked.reason === 'chat_text_empty' ? 'The message is empty' : `The message is longer than ${checked.max} characters`;
      throw new BackendError('VALIDATION_FAILED', message, { reason: checked.reason, field: 'text', max: checked.max });
    }
    // The flood checks and their record in one synchronous step: parallel sends of one player cannot all pass.
    const now = this.d.clock.now();
    const key = chatDuplicateKey(checked.text);
    this.assertNotFlooding(playerId, key, now, ctx?.remoteAddress ? addressLimitKey(ctx.remoteAddress) : null);
    const masked = maskChatWords(checked.text, this.cfg.words);
    const nickname = send.nickname;
    const level = send.level;
    return this.d.mutex.run(`chat:${p.channel}`, async () => {
      // (the membership again: the clan may have changed while the request waited for the channel)
      if (!this.mayRead(playerId, p.channel)) throw new BackendError('FORBIDDEN', 'Only the members of the clan read and write its chat', { reason: 'chat_not_member', channel: p.channel });
      // (and the account and the mute: an account deleted or a player muted while the request waited writes nothing)
      if (!(await this.d.storage.get(C.accounts, playerId))) throw new BackendError('NOT_FOUND', 'No such account', { reason: 'account_not_found' });
      const muted = this.muteOf(playerId);
      if (muted) throw new BackendError('FORBIDDEN', 'An operator muted this player in the chat', { reason: 'chat_muted', until: muted.until });
      const at = this.d.clock.now();
      const seq = Math.max(at * 1000, this.lastSeq + 1);
      this.lastSeq = seq;
      const clan: ClanBrief | null = this.directory.briefOf(playerId);
      const msg: StoredChatMessage = {
        v: 1,
        messageId: `m${seq.toString(36)}`,
        channel: p.channel,
        seq,
        at,
        playerId,
        nickname,
        level,
        clan,
        text: masked.text,
        ...(masked.masked ? { original: checked.text } : {}),
      };
      const list = this.channelState(p.channel);
      list.push(msg);
      const dropped = list.splice(0, Math.max(0, list.length - this.keptIn(p.channel)));
      try {
        await this.d.storage.batch([putOp(C.chatMessages, msg.messageId, msg), ...dropped.map((m) => deleteOp(C.chatMessages, m.messageId))]);
      } catch (err) {
        const i = list.indexOf(msg);
        if (i >= 0) list.splice(i, 1);
        list.unshift(...dropped);
        throw err;
      }
      for (const m of dropped) this.channelOf.delete(m.messageId);
      this.channelOf.set(msg.messageId, p.channel);
      const view = this.view(msg);
      this.d.hub.send(this.recipients(p.channel), 'chat.message', { message: view }, ctx);
      // The sender has seen its channel up to its own message.
      void this.moveRead(playerId, p.channel, seq).catch((err) => this.d.logger.warn('chat read position not stored', { playerId, err: err instanceof Error ? err.message : String(err) }));
      return { message: view };
    });
  }

  /** RATE_LIMITED when the player (or its address) sends too much or the same again; else the message is counted. */
  private assertNotFlooding(playerId: PlayerId, key: string, now: number, address: string | null): void {
    const c = this.cfg;
    const fromAddress = address !== null && c.addressBurst > 0 ? (this.recentByAddress.get(address) ?? []).filter((at) => now - at < c.windowMs) : null;
    const keep = Math.max(c.windowMs, c.duplicateWindowMs, c.minIntervalMs);
    const recent = (this.recent.get(playerId) ?? []).filter((r) => now - r.at < keep);
    const last = recent[recent.length - 1];
    if (last && now - last.at < c.minIntervalMs) {
      this.recent.set(playerId, recent);
      throw new BackendError('RATE_LIMITED', 'You are sending messages too fast', { reason: 'chat_rate_limited', retryAfterMs: Math.max(1, c.minIntervalMs - (now - last.at)) });
    }
    const inWindow = recent.filter((r) => now - r.at < c.windowMs);
    if (inWindow.length >= c.burst) {
      this.recent.set(playerId, recent);
      throw new BackendError('RATE_LIMITED', 'You are sending messages too fast', { reason: 'chat_rate_limited', retryAfterMs: Math.max(1, c.windowMs - (now - inWindow[0].at)) });
    }
    const same = key === '' ? undefined : recent.find((r) => r.key === key && now - r.at < c.duplicateWindowMs);
    if (same) {
      this.recent.set(playerId, recent);
      throw new BackendError('RATE_LIMITED', 'You sent the same message a moment ago', { reason: 'chat_duplicate', retryAfterMs: Math.max(1, c.duplicateWindowMs - (now - same.at)) });
    }
    if (fromAddress && address !== null) {
      if (fromAddress.length >= c.addressBurst) {
        this.recentByAddress.set(address, fromAddress);
        this.recent.set(playerId, recent);
        throw new BackendError('RATE_LIMITED', 'Too many messages from this network', { reason: 'chat_rate_limited', scope: 'address', retryAfterMs: Math.max(1, c.windowMs - (now - fromAddress[0])) });
      }
      fromAddress.push(now);
      this.recentByAddress.set(address, fromAddress);
    }
    recent.push({ at: now, key });
    this.recent.set(playerId, recent);
  }

  /** chat.subscribe */
  async subscribe(playerId: PlayerId, channels: readonly ChatChannelId[]): Promise<{ subscribed: string[] }> {
    for (const channel of channels) await this.assertAccess(playerId, channel);
    let mine = this.subscriptions.get(playerId);
    if (!mine) this.subscriptions.set(playerId, (mine = new Set()));
    for (const channel of channels) {
      mine.add(channel);
      let set = this.subscribers.get(channel);
      if (!set) this.subscribers.set(channel, (set = new Set()));
      set.add(playerId);
    }
    // A player that is not present (an HTTP client that does not poll its events, a connection that closed while the
    // request ran) gets no offline report: its grace starts now, so the subscription cannot outlive it for ever.
    if (this.d.hub.isOnline(playerId)) this.offlineSince.delete(playerId);
    else if (!this.offlineSince.has(playerId)) this.offlineSince.set(playerId, this.d.clock.now());
    return { subscribed: this.subscribedOf(playerId) };
  }

  /** chat.unsubscribe (no `channels`: every channel). */
  unsubscribe(playerId: PlayerId, channels?: readonly ChatChannelId[]): { subscribed: string[] } {
    const mine = this.subscriptions.get(playerId);
    for (const channel of channels ?? [...(mine ?? [])]) this.dropSubscription(playerId, channel);
    return { subscribed: this.subscribedOf(playerId) };
  }

  /** chat.markRead */
  async markRead(playerId: PlayerId, channel: ChatChannelId, seq: number): Promise<{ channel: string; readSeq: number; unread: number }> {
    await this.assertAccess(playerId, channel);
    const readSeq = await this.moveRead(playerId, channel, seq);
    return { channel, readSeq, unread: this.unreadIn(channel, readSeq, playerId) };
  }

  /** The channels the player is subscribed to and still reads (a lost clan channel is dropped here). */
  private subscribedOf(playerId: PlayerId): string[] {
    const mine = this.subscriptions.get(playerId);
    if (!mine) return [];
    for (const channel of [...mine]) if (!this.mayRead(playerId, channel)) this.dropSubscription(playerId, channel);
    return [...(this.subscriptions.get(playerId) ?? [])].sort((x, y) => (x === CHAT_GLOBAL ? -1 : y === CHAT_GLOBAL ? 1 : x.localeCompare(y)));
  }

  private dropSubscription(playerId: PlayerId, channel: ChatChannelId): void {
    const mine = this.subscriptions.get(playerId);
    mine?.delete(channel);
    if (mine && mine.size === 0) {
      this.subscriptions.delete(playerId);
      this.offlineSince.delete(playerId);
    }
    const set = this.subscribers.get(channel);
    set?.delete(playerId);
    if (set && set.size === 0) this.subscribers.delete(channel);
  }

  /** The subscribers of a channel who read it now (membership checked at every delivery). */
  private recipients(channel: ChatChannelId): PlayerId[] {
    return [...(this.subscribers.get(channel) ?? [])].filter((pid) => this.mayRead(pid, channel));
  }

  // ------------------------------------------------------------------------------------------------ presence and sweep

  /** The player's last connection closed: its subscriptions end after the grace period unless it comes back. */
  playerOffline(playerId: PlayerId): void {
    if (this.subscriptions.has(playerId) && !this.offlineSince.has(playerId)) this.offlineSince.set(playerId, this.d.clock.now());
  }

  playerOnline(playerId: PlayerId): void {
    this.offlineSince.delete(playerId);
  }

  /**
   * Background work (BackendCore.tick): lapsed subscriptions, the read positions due to be stored; every minute the
   * channels of gone clans, old mutes and what the memory keeps of absent players.
   */
  async sweep(now: number): Promise<void> {
    for (const [pid, since] of [...this.offlineSince]) {
      if (this.d.hub.isOnline(pid)) this.offlineSince.delete(pid);
      else if (now - since >= this.cfg.subscriptionGraceMs) for (const channel of [...(this.subscriptions.get(pid) ?? [])]) this.dropSubscription(pid, channel);
    }
    if (this.dirtyReads.size > 0 && now - this.lastReadFlush >= READ_FLUSH_MS / 3) {
      this.lastReadFlush = now;
      // (a failed write keeps them for the next flush)
      await this.flushReads(false).catch((err) => this.d.logger.warn('chat read positions not stored', { err: err instanceof Error ? err.message : String(err) }));
    }
    if (now - this.lastHousekeeping < HOUSEKEEPING_MS || !this.loading) return;
    this.lastHousekeeping = now;
    await this.directory.ready();
    for (const pid of [...this.subscriptions.keys()]) this.subscribedOf(pid);
    // The flood records past their windows, the read positions of absent players (stored: read again when needed).
    const c = this.cfg;
    const keep = Math.max(c.windowMs, c.duplicateWindowMs, c.minIntervalMs);
    for (const [pid, list] of [...this.recent]) if (list.length === 0 || now - list[list.length - 1].at >= keep) this.recent.delete(pid);
    for (const [address, list] of [...this.recentByAddress]) if (list.length === 0 || now - list[list.length - 1] >= c.windowMs) this.recentByAddress.delete(address);
    for (const pid of [...this.reads.keys()]) if (!this.dirtyReads.has(pid) && !this.subscriptions.has(pid) && !this.d.hub.isOnline(pid)) this.reads.delete(pid);
    const ops: StorageBatchOp[] = [];
    for (const [channel, list] of [...this.channels]) {
      const p = parseChatChannel(channel);
      if (!p || p.kind !== 'clan' || this.directory.clan(p.clanId)) continue;
      // The clan is gone (disbanded by its leader, an operator or the deletion of its last member): so is its chat.
      for (const m of list) {
        ops.push(deleteOp(C.chatMessages, m.messageId));
        this.channelOf.delete(m.messageId);
      }
      this.channels.delete(channel);
      for (const pid of [...(this.subscribers.get(channel) ?? [])]) this.dropSubscription(pid, channel);
    }
    for (const [pid, m] of [...this.mutes]) {
      if (m.until > now) continue;
      this.mutes.delete(pid);
      ops.push(deleteOp(C.chatMutes, pid));
    }
    for (let i = 0; i < ops.length; i += 500) await this.d.storage.batch(ops.slice(i, i + 500));
  }

  // ------------------------------------------------------------------------------------------------ operators

  /** Every kept message, newest first, optionally of one channel / one player / containing a text (the console). */
  async rows(filter: { channel?: string; playerId?: PlayerId; query?: string } = {}): Promise<ChatMessageRow[]> {
    await this.init();
    const q = filter.query ? filter.query.toLowerCase() : null;
    const out: ChatMessageRow[] = [];
    for (const [channel, list] of this.channels) {
      if (filter.channel !== undefined && channel !== filter.channel) continue;
      for (const m of list) {
        if (filter.playerId !== undefined && m.playerId !== filter.playerId) continue;
        if (q && !m.text.toLowerCase().includes(q) && !(m.original ?? '').toLowerCase().includes(q) && !m.nickname.toLowerCase().includes(q)) continue;
        out.push({ ...this.view(m), original: m.original ?? null });
      }
    }
    return out.sort((a, b) => b.seq - a.seq);
  }

  /** The channels that hold messages, with their counts and newest message (the console). */
  async channelRows(): Promise<{ channel: string; kind: 'global' | 'clan'; clan: ClanBrief | null; messages: number; lastAt: number | null; subscribers: number }[]> {
    await this.init();
    await this.directory.ready();
    const ids = new Set<ChatChannelId>([CHAT_GLOBAL, ...this.channels.keys()]);
    return [...ids].map((channel) => {
      const p = parseChatChannel(channel);
      const list = this.channels.get(channel) ?? [];
      return {
        channel,
        kind: p?.kind ?? 'global',
        clan: p && p.kind === 'clan' ? this.directory.brief(p.clanId) : null,
        messages: list.length,
        lastAt: list.length > 0 ? list[list.length - 1].at : null,
        subscribers: this.recipients(channel).length,
      };
    });
  }

  async message(messageId: string): Promise<ChatMessageRow | null> {
    await this.init();
    const channel = this.channelOf.get(messageId);
    const m = channel ? this.channels.get(channel)?.find((x) => x.messageId === messageId) : undefined;
    return m ? { ...this.view(m), original: m.original ?? null } : null;
  }

  /** An operator removes a message: gone from storage and the channel, the subscribers get chat.deleted. */
  async deleteMessage(messageId: string): Promise<ChatMessageRow> {
    await this.init();
    const channel = this.channelOf.get(messageId);
    if (!channel) throw new BackendError('NOT_FOUND', 'No such chat message', { reason: 'chat_message_not_found', messageId });
    return this.d.mutex.run(`chat:${channel}`, async () => {
      const list = this.channels.get(channel) ?? [];
      const i = list.findIndex((m) => m.messageId === messageId);
      if (i < 0) throw new BackendError('NOT_FOUND', 'No such chat message', { reason: 'chat_message_not_found', messageId });
      const m = list[i];
      await this.d.storage.delete(C.chatMessages, messageId);
      list.splice(i, 1);
      this.channelOf.delete(messageId);
      this.d.hub.send(this.recipients(channel), 'chat.deleted', { channel, messageIds: [messageId], reason: 'moderator' });
      return { ...this.view(m), original: m.original ?? null };
    });
  }

  /**
   * An operator restored a damaged message or mute (admin server.repairDocument): the channel or the mutes, loaded
   * without it, take it in (a message at its place by seq; beyond the channel's history the oldest one goes, from the
   * storage too, as with a new message).
   */
  async repaired(collection: string, value: unknown): Promise<void> {
    await this.init();
    if (collection === C.chatMutes) {
      const m = value as ChatMuteRecord;
      if (m && typeof m.playerId === 'string' && typeof m.until === 'number') this.mutes.set(m.playerId, m);
      return;
    }
    const m = value as StoredChatMessage;
    if (collection !== C.chatMessages || !m || typeof m.messageId !== 'string' || typeof m.channel !== 'string' || !Number.isSafeInteger(m.seq)) return;
    await this.d.mutex.run(`chat:${m.channel}`, async () => {
      if (this.channelOf.has(m.messageId)) return;
      const list = this.channelState(m.channel);
      const at = list.findIndex((x) => x.seq > m.seq);
      list.splice(at < 0 ? list.length : at, 0, m);
      this.channelOf.set(m.messageId, m.channel);
      this.lastSeq = Math.max(this.lastSeq, m.seq);
      const dropped = list.splice(0, Math.max(0, list.length - this.keptIn(m.channel)));
      for (const x of dropped) this.channelOf.delete(x.messageId);
      if (dropped.length > 0) await this.d.storage.batch(dropped.map((x) => deleteOp(C.chatMessages, x.messageId)));
    });
  }

  /** An operator mutes a player for `durationMs` (at most config.chat.maxMuteMs). */
  async mute(playerId: PlayerId, durationMs: number, by: string, reason: string | null): Promise<ChatMuteRecord> {
    await this.init();
    const now = this.d.clock.now();
    const rec: ChatMuteRecord = { v: 1, playerId, until: now + Math.max(1, Math.min(durationMs, this.cfg.maxMuteMs)), at: now, by, reason };
    await this.d.storage.put(C.chatMutes, playerId, rec);
    this.mutes.set(playerId, rec);
    return { ...rec };
  }

  /** An operator ends a mute (INVALID_STATE 'chat_not_muted' when there is none). */
  async unmute(playerId: PlayerId): Promise<ChatMuteRecord> {
    await this.init();
    const m = this.muteOf(playerId);
    if (!m) throw new BackendError('INVALID_STATE', 'The player is not muted', { reason: 'chat_not_muted', playerId });
    await this.d.storage.delete(C.chatMutes, playerId);
    this.mutes.delete(playerId);
    return m;
  }

  /** The mutes in force, the longest first. */
  async activeMutes(): Promise<ChatMuteRecord[]> {
    await this.init();
    const now = this.d.clock.now();
    return [...this.mutes.values()].filter((m) => m.until > now).sort((a, b) => b.until - a.until).map((m) => ({ ...m }));
  }

  // ------------------------------------------------------------------------------------------------ account deletion

  /** The deletion's batch: the player's messages, read positions and mute. */
  async deletionOps(playerId: PlayerId): Promise<StorageBatchOp[]> {
    await this.init();
    const ops: StorageBatchOp[] = [];
    for (const list of this.channels.values()) for (const m of list) if (m.playerId === playerId) ops.push(deleteOp(C.chatMessages, m.messageId));
    if (this.reads.has(playerId) || (await this.d.storage.get(C.chatReads, playerId))) ops.push(deleteOp(C.chatReads, playerId));
    if (this.mutes.has(playerId)) ops.push(deleteOp(C.chatMutes, playerId));
    return ops;
  }

  /**
   * After the deletion's batch: the player's messages leave their channels (chat.deleted to the subscribers), and what
   * requests still under way stored after the batch (a message, a read position) is deleted from the storage too.
   */
  forget(playerId: PlayerId): void {
    const late: StorageBatchOp[] = [];
    for (const [channel, list] of this.channels) {
      const gone = list.filter((m) => m.playerId === playerId);
      if (gone.length === 0) continue;
      this.channels.set(
        channel,
        list.filter((m) => m.playerId !== playerId),
      );
      for (const m of gone) {
        this.channelOf.delete(m.messageId);
        late.push(deleteOp(C.chatMessages, m.messageId));
      }
      this.d.hub.send(this.recipients(channel), 'chat.deleted', { channel, messageIds: gone.map((m) => m.messageId), reason: 'account_deleted' });
    }
    for (const channel of [...(this.subscriptions.get(playerId) ?? [])]) this.dropSubscription(playerId, channel);
    this.offlineSince.delete(playerId);
    this.reads.delete(playerId);
    this.dirtyReads.delete(playerId);
    this.mutes.delete(playerId);
    this.recent.delete(playerId);
    // (after a flush of the read positions under way: it may have taken this player's before the deletion)
    late.push(deleteOp(C.chatReads, playerId));
    void Promise.resolve(this.flushingReads)
      .catch(() => undefined)
      .then(() => this.d.storage.batch(late))
      .catch((err) => this.d.logger.warn('chat: the leftovers of a deleted account were not deleted', { playerId, err: err instanceof Error ? err.message : String(err) }));
  }

  /**
   * The player's nickname changed (its own rename, an operator's): the kept messages show the new one (an operator who
   * renames an offensive nickname removes it from the chat too).
   */
  async renamed(playerId: PlayerId): Promise<number> {
    await this.init();
    const a = await this.d.storage.get<{ nickname?: string | null; displayName: string }>(C.accounts, playerId);
    if (!a) return 0;
    const nickname = a.nickname ?? a.displayName;
    let changed = 0;
    for (const [channel, list] of [...this.channels]) {
      if (!list.some((m) => m.playerId === playerId && m.nickname !== nickname)) continue;
      await this.d.mutex.run(`chat:${channel}`, async () => {
        const mine = (this.channels.get(channel) ?? []).filter((m) => m.playerId === playerId && m.nickname !== nickname);
        if (mine.length === 0) return;
        await this.d.storage.batch(mine.map((m) => putOp(C.chatMessages, m.messageId, { ...m, nickname })));
        // (in place: the channel's list as it is after the write)
        const ids = new Set(mine.map((m) => m.messageId));
        const list = this.channels.get(channel) ?? [];
        for (let i = 0; i < list.length; i++) if (ids.has(list[i].messageId)) list[i] = { ...list[i], nickname };
        changed += mine.length;
      });
    }
    return changed;
  }

  /** A stored message as clients see it: no storage version, no unmasked text; the sender's clan as it is now. */
  private view(m: StoredChatMessage): ChatMessage {
    const clan = m.clan ? this.directory.brief(m.clan.clanId) : null;
    return { messageId: m.messageId, channel: m.channel, seq: m.seq, at: m.at, playerId: m.playerId, nickname: m.nickname, level: m.level, clan, text: m.text };
  }
}

/** The cursor's tag of a channel ('g', or 'c<clanId>': cursor parts are letters and digits). */
function cursorTag(channel: ChatChannelId): string {
  const p = parseChatChannel(channel);
  return p && p.kind === 'clan' ? `c${p.clanId}` : 'g';
}
